import React, { useEffect } from 'react';

/**
 * FullScreenPanel — a floating popup dialog for view/edit/add experiences.
 * Renders as a fixed overlay above the page (dimmed, blurred backdrop) with
 * the panel centered. The header stays put while the body scrolls, and an
 * optional footer is pinned to the bottom of the dialog.
 *
 * The ✕ / back buttons and Esc close it. Clicking the backdrop intentionally
 * does NOT close it, so an accidental click can't discard a half-filled form.
 *
 * (Historically this component rendered inline in the page flow; the API is
 * unchanged, only the presentation moved to a popup.)
 *
 * @param {boolean}   isOpen      Whether the panel is shown.
 * @param {function}  onClose     Called by the back arrow, ✕ button, and Esc.
 * @param {string}    title       Heading shown in the panel's bar.
 * @param {string}    [subtitle]  Secondary line under the title (e.g. a code).
 * @param {ReactNode} [headerRight] Extra controls on the right of the bar.
 * @param {ReactNode} [footer]    Pinned footer content (usually action buttons).
 * @param {boolean}   [fullHeight] Keep the dialog at full height regardless of
 *                                 content, so switching between sections of
 *                                 different lengths never moves the dialog.
 * @param {ReactNode} children    Panel body.
 */
const FullScreenPanel = ({
  isOpen,
  onClose,
  title,
  subtitle,
  headerRight = null,
  footer = null,
  fullHeight = false,
  children,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Lock the page scroll behind the dialog while it is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    // Overlay covers the content area only: on desktop it starts after the
    // sidebar (live width via --app-sidebar-w) so the menu stays visible and
    // clickable; on mobile the sidebar is off-canvas, so it spans the screen.
    <div
      className="fixed inset-y-0 right-0 left-0 lg:left-[var(--app-sidebar-w,16rem)] z-30"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Dimmed backdrop (inert — see note above). */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      {/* Every dialog is the full width of the content area and starts at
          the top, with the same margin all round, whatever its height. */}
      <div className="relative z-10 flex h-full items-start justify-center p-3 sm:p-6">
        <section
          className={`flex w-full ${fullHeight ? 'h-full' : 'max-h-full'} flex-col overflow-hidden bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 shadow-2xl`}
          role="region"
          aria-label={title}
        >
          {/* Header bar — fixed at the top of the dialog. */}
          <header className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 bg-gray-50/95 dark:bg-slate-900/95 px-4 sm:px-6">
            <div className="flex items-center gap-3 py-3">
              <button
                type="button"
                onClick={onClose}
                aria-label="Back"
                className="flex-shrink-0 -ml-1 p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-slate-100 truncate">{title}</h2>
                {subtitle ? <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{subtitle}</p> : null}
              </div>
              {headerRight}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex-shrink-0 p-2 rounded-lg text-gray-400 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </header>

          {/* Body — scrolls inside the dialog. form-compact keeps all field
              sizes consistent with Client Details. */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 sm:py-6 form-compact">
            {children}
          </div>

          {/* Pinned footer */}
          {footer ? (
            <footer className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-gray-50/95 dark:bg-slate-900/95 px-4 sm:px-6">
              <div className="py-3 flex flex-wrap items-center justify-end gap-2">
                {footer}
              </div>
            </footer>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default FullScreenPanel;
